import type * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { X } from "@phosphor-icons/react/X";

import { Option } from "../../../../option";
import type { BaseComponentProps, QueryField } from "../types";

export interface TextFilterProps extends BaseComponentProps {
	/** Field configuration */
	field: QueryField;
	/** Current selected values */
	value: string[];
	/** Callback when selection changes */
	onChange: (value: string[]) => void;
}

export function TextFilter({
	field,
	value,
	onChange,
	disabled = false,
}: TextFilterProps): React.JSX.Element | null {
	// All text filters are categorical with predefined options
	if (!field.options) {
		console.warn(`Text field ${field.id} is missing options array`);
		return null;
	}

	const handleChange = (_: React.SyntheticEvent, newValue: string[]) => {
		onChange(newValue);
	};

	const handleClear = () => {
		onChange([]);
	};

	const hasValues = value && value.length > 0;

	return (
		<Stack spacing={1}>
			{/* Header with label and clear button */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}>
				<Typography
					variant="subtitle2"
					sx={{ fontWeight: "medium" }}>
					{field.displayName}
				</Typography>
				{hasValues && (
					<Button
						variant="text"
						size="small"
						onClick={handleClear}
						disabled={disabled}
						startIcon={<X size={14} />}
						sx={{ fontSize: "0.75rem", py: 0.5, color: "text.secondary" }}
						aria-label={`Clear ${field.displayName} filter`}>
						Clear
					</Button>
				)}
			</Box>

			{/* Autocomplete field */}
			<Autocomplete
				multiple
				size="small"
				options={field.options}
				value={value || []}
				onChange={handleChange}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						fullWidth
						size="small"
						placeholder={
							value?.length > 0
								? ""
								: `Select ${field.displayName.toLowerCase()}`
						}
					/>
				)}
				renderOption={(props, option) => (
					<Option
						{...props}
						key={option}
						value={option}>
						{option}
					</Option>
				)}
				renderTags={(tagValue, getTagProps) =>
					tagValue.map((option, index) => (
						<Chip
							variant="outlined"
							label={option}
							{...getTagProps({ index })}
							key={option}
							size="small"
						/>
					))
				}
			/>
		</Stack>
	);
}
